import { Dispatch, SetStateAction, useCallback, useContext, useState } from "react";
import { IPaymentPayload } from "../..";
import "./style.scss";
import { ExclamationIconSvgPPP, GreenTickIconSvgPPP } from "../svgImages";
import { debounce } from "lodash";
import { verifyGstNumber } from "../../../../api/verification.api";
import SnackbarContext from "../../../../context/snackbar.context";

interface IGstDetailsCard {
  fieldsDisabled: boolean;
  paymentPayload: IPaymentPayload;
  setPaymentPayload: Dispatch<SetStateAction<IPaymentPayload>>;
  paymentGoAhead: boolean;
  setPaymentGoAhead: Dispatch<SetStateAction<boolean>>;
  gstErrorsObj: { gstNumber: boolean, gstName: boolean};
  setGstErrorsObj: Dispatch<SetStateAction<any>>;
}

const GstDetailsCard: React.FC<IGstDetailsCard> = (props) => {
  const snackbarCtx = useContext(SnackbarContext);
  const { fieldsDisabled, paymentPayload, setPaymentPayload, setPaymentGoAhead, gstErrorsObj, setGstErrorsObj } = props;
  const [loading, setLoading] = useState<boolean>(false);

  const debouncedFn = debounce(async (value) => {
    const response = await verifyGstNumber(value);
    setLoading(false);
    setGstErrorsObj({ gstNumber: !response?.status, gstName: false });
    setPaymentPayload({ ...paymentPayload, gstNumber: value?.toUpperCase(), gstName: (response?.business_name || ''), firstName: (response?.business_name || ''), pan: (response?.pan_number || '') });
    if(response?.status) snackbarCtx.toggleSnackbar({
      message: "Business name updated below for provided GST number.",
      type: "SUCCESS",
      visible: true,
    });
  }, 500);

  const handleGstInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const trimmedValue = name === "gstNumber" ? value?.toUpperCase().trim() : value?.toUpperCase();
    setPaymentPayload((prevPayload) => ({ ...prevPayload, [name]: trimmedValue }));

    const gstNumberRegex = /^\d{2}[a-zA-Z]{5}\d{4}[a-zA-Z]\d[Zz][a-zA-Z\d]$/;
    const gstNameRegex = /^(?=.*[a-zA-Z])[a-zA-Z\s().\-0-9\/&]+$/;

    let errorTriggered = false;

    if (name === "gstNumber") {
      setGstErrorsObj({ ...gstErrorsObj, gstNumber: !gstNumberRegex.test(trimmedValue) });
      errorTriggered = !gstNumberRegex.test(trimmedValue);
      if (errorTriggered) {
        setLoading(false);
        debouncedFn.cancel();
      } else {
        setLoading(true);
        debouncedFn(trimmedValue);
      }      
    } else {
      setGstErrorsObj({...gstErrorsObj, gstName: !gstNameRegex.test(trimmedValue)});
      errorTriggered = !gstNameRegex.test(trimmedValue);
    }

    if (errorTriggered) setPaymentGoAhead(false);

    // to check if both fields are empty then reset the error
    const otherField = name === "gstNumber" ? "gstName" : "gstNumber";
    if (value === '' && !paymentPayload[otherField]) {
      setGstErrorsObj({ gstNumber: false, gstName: false });
    }
  }, [paymentPayload, gstErrorsObj]);

  return (
    <div className="gst-details-card-wrapper">
      <div className="gst-text-wrapper">
        <span className="primary-text">I have a GST Number</span>
        <span className="secondary-text">Kindly submit GST details for seamless filing of your tax return</span>
      </div>
      <div className="gst-input-details-wrapper">
        <div className="relative-wrapper">
          <input type="text" disabled={fieldsDisabled || loading} onChange={handleGstInput} name="gstNumber" placeholder="GSTIN" value={paymentPayload?.gstNumber || ""}/>
          {gstErrorsObj?.gstNumber && !loading && (<span className="abs-wrapper" title="Invalid GSTIN"><ExclamationIconSvgPPP /></span>)}
          {loading && <span className="abs-wrapper loader" title="loading"></span>}
          {!gstErrorsObj?.gstNumber && !!paymentPayload?.gstNumber && !loading && (<span className="abs-wrapper" title="Valid GSTIN"><GreenTickIconSvgPPP /></span>)}
        </div>
        <div className="relative-wrapper">
          <input type="text" autoComplete="nope" disabled={fieldsDisabled || (!!paymentPayload?.gstNumber && !gstErrorsObj?.gstNumber)} onChange={handleGstInput} name="gstName" placeholder="Name" value={paymentPayload?.gstName || ""}/>
          {gstErrorsObj?.gstName && (<span className="abs-wrapper" title="Invalid Name"><ExclamationIconSvgPPP /></span>)}
        </div>
      </div>
    </div>
  );
};

export default GstDetailsCard;
